---
title: Share a persona
summary: Make a persona discoverable with tags, per-user shares, or by publishing it to everyone.
visibility: authenticated
audience: author-admin
section: How-to
order: 64
last_verified: 2026-07-21
owner: docs
---

# Share a persona

By default a persona is visible only to the person who created it. There are three ways to
make it available to others, plus tags to keep everyone's lists tidy.

## Tag your personas

Click the persona card's **Edit** button to open the **Workshop**, then in its
**Identity** section add **tags** (for example `client-x`, `seasonal`, `favorite`). On
the **Personas** page you can then filter by tag and by scope
(**All**, **Mine**, **Public**, **Shared with me**). Tags are per-persona and suggestions
come from tags already in use. Tags organize and filter — they don't grant access.

## Ways a persona becomes visible to others

| Path | What it does | Who can do it |
|---|---|---|
| **Owner** | The creator always sees their persona. | Automatic |
| **Share with specific people** | Grants named users access. | Manager or admin |
| **Make public** | Shows the persona to **everyone** on the deployment (read-only for non-owners). | Admin only |

### Share with specific people

An administrator (or manager) opens the **Sharing** page under Admin, finds the persona,
and uses the share action to pick one or more users to grant access to. Sharing grants
**access** — whether a recipient can also **edit** the persona is decided by their own
role's persona-edit permission, so most users can edit personas shared with them while
view-only roles cannot. Deleting a persona always remains the owner's call. Shares can be
revoked the same way. These personas show up under the **Shared with me** scope for the
recipient.

### Make public

In the Workshop's **Identity** section, an admin can tick **"Public — visible to all
users."** The persona then appears in everyone's list (under the **Public** scope)
without individual shares. Only administrators can change this setting; non-admins only
see the checkbox at all once a persona is already public, and it's shown disabled.

## See also

- [Personas, bundles, and workflows](/help/concepts/personas-bundles-workflows) — the ownership model.
- [Glossary](/help/reference/glossary) — *public*, *shared*, *scope*, *tag*.
